import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useFollowSystem } from '@/hooks/useFollowSystem';
import ProfileGrid from '@/components/Profile/ProfileGrid';
import PetCard from '@/components/Profile/PetCard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecondaryButton, GlassButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Link as LinkIcon, Calendar, Users, UserPlus, UserMinus, Grid3X3, Heart, Camera, MessageCircle, Share2, MoreHorizontal, Lock, Clock, UserCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { copyToClipboard } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import profileHeroBg from '@/assets/profile-hero-bg.jpg';
const UserProfile = () => {
  const {
    username
  } = useParams<{
    username: string;
  }>();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    profile,
    pets,
    posts,
    loading,
    isOwnProfile,
    canViewContent,
    refetchProfile
  } = useUserProfile(username || '');

  // Follow system hook (only for external profiles)
  const followSystem = useFollowSystem({
    targetUserId: profile?.user_id || '',
    targetUsername: profile?.username || '',
    isPrivateProfile: profile?.is_private || false,
    onSuccess: refetchProfile
  });
  const [followLoading, setFollowLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  // Initialize follow system when profile loads
  useEffect(() => {
    if (profile && !isOwnProfile) {
      followSystem.initializeStatus();
    }
  }, [profile, isOwnProfile]);

  // Redirect to auth if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect to own profile if viewing own username
  if (!loading && isOwnProfile) {
    return <Navigate to="/app/profile" replace />;
  }
  const handleSendMessage = async () => {
    if (!canViewContent && profile?.is_private) {
      toast.error('Non puoi inviare messaggi a profili privati che non segui');
      return;
    }
    
    if (!user || !profile) return;

    try {
      // Create or find existing conversation
      const { data: existingConversations, error: fetchError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations (
            id,
            type,
            conversation_participants!inner (
              user_id
            )
          )
        `)
        .eq('user_id', user.id);

      if (fetchError) throw fetchError;

      // Find a direct conversation with this user
      let conversationId = null;
      
      for (const participation of existingConversations || []) {
        const conversation = participation.conversations;
        if (conversation?.type === 'direct') {
          const participants = conversation.conversation_participants;
          if (participants.length === 2) {
            const otherParticipant = participants.find(p => p.user_id !== user.id);
            if (otherParticipant?.user_id === profile.user_id) {
              conversationId = conversation.id;
              break;
            }
          }
        }
      }

      // If no conversation exists, create one
      if (!conversationId) {
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            type: 'direct',
            name: null,
            created_by: user.id
          })
          .select()
          .single();

        if (createError) throw createError;

        conversationId = newConversation.id;

        // Add participants
        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert([
            { conversation_id: conversationId, user_id: user.id },
            { conversation_id: conversationId, user_id: profile.user_id }
          ]);

        if (participantsError) throw participantsError;
      }

      // Navigate to the conversation
      window.location.href = `/app/chat/${conversationId}`;
    } catch (error) {
      console.error('Error creating/finding conversation:', error);
      toast.error('Errore nell\'aprire la chat');
    }
  };
  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/app/user/${profile?.username}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Profilo di ${profile?.display_name || profile?.username}`,
          text: `Guarda il profilo di ${profile?.display_name || profile?.username} su PetConnect`,
          url: profileUrl
        });
        return;
      } catch (error) {
        console.error('Errore durante la condivisione:', error);
        // fallback al dialog di copia link
      }
    }
    setShareDialogOpen(true);
  };
  const handleToggleFollow = async () => {
    if (!profile || isOwnProfile) return;
    setFollowLoading(true);
    try {
      await followSystem.handleFollowAction();
    } finally {
      setFollowLoading(false);
    }
  };
  const handleMoreOptions = () => {
    toast.info('Opzioni aggiuntive in arrivo!');
  };
  const getFollowButtonContent = () => {
    if (followLoading || followSystem.loading) {
      return <>
          <div className="w-4 h-4 mr-2 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          Caricamento...
        </>;
    }
    const buttonState = followSystem.getButtonState();
    if (followSystem.isFollowing) {
      return <>
          <UserMinus className="w-4 h-4 mr-2" />
          {buttonState.text}
        </>;
    }
    switch (followSystem.followRequestStatus) {
      case 'pending':
        return <>
            <Clock className="w-4 h-4 mr-2" />
            {buttonState.text}
          </>;
      case 'rejected':
        return <>
            <X className="w-4 h-4 mr-2" />
            {buttonState.text}
          </>;
      default:
        return <>
            <UserPlus className="w-4 h-4 mr-2" />
            {buttonState.text}
          </>;
    }
  };
  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };
  if (authLoading || loading) {
    return <div className="max-w-4xl mx-auto p-4 space-y-6">
        <div className="h-8 w-32 bg-muted rounded animate-pulse" />
        <Card className="glass border-white/20">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-32 h-32 bg-muted rounded-full animate-pulse" />
              <div className="flex-1 space-y-4">
                <div className="space-y-2">
                  <div className="h-8 bg-muted rounded w-48 animate-pulse" />
                  <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                </div>
                <div className="flex gap-6">
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="text-center space-y-1">
                    <div className="h-6 w-12 bg-muted rounded animate-pulse" />
                    <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-3 gap-2">
          {[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-muted rounded animate-pulse" />)}
        </div>
      </div>;
  }
  if (!profile) {
    return <div className="max-w-4xl mx-auto p-4">
        <Link to="/app/explore">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
        </Link>
        <div className="text-center py-16">
          <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Utente non trovato</h3>
          <p className="text-muted-foreground">
            L'utente @{username} non esiste o non è disponibile.
          </p>
        </div>
      </div>;
  }
  return <div 
    className="cosmic-background min-h-screen"
    style={{ backgroundImage: `url(${profileHeroBg})` }}
  >
    <div className="max-w-4xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Back Button */}
      <Link to="/app/explore">
        <Button variant="ghost" className="mb-4 text-white hover:text-primary">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
      </Link>

      {/* Profile Header */}
      <Card className="glass border-white/20 mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}
            <div className="flex justify-center md:justify-start">
              <Avatar className="w-32 h-32 ring-4 ring-primary/30">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
                  {profile.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Profile Info */}
            <div className="flex-1 space-y-4">
              {/* Header Row */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-white">
                      {profile.display_name || profile.username}
                    </h1>
                    {profile.is_private && <Badge variant="secondary" className="flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Privato
                      </Badge>}
                  </div>
                  <p className="text-muted-foreground">@{profile.username}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-2 md:ml-auto">
                  <Button onClick={handleToggleFollow} disabled={followLoading || followSystem.loading || followSystem.followRequestStatus === 'rejected'} className={followSystem.isFollowing ? "bg-muted hover:bg-muted/80 text-white" : followSystem.followRequestStatus === 'pending' ? "bg-yellow-600 hover:bg-yellow-700 text-white" : followSystem.followRequestStatus === 'rejected' ? "bg-red-600/50 text-red-200 cursor-not-allowed" : ""}>
                    {getFollowButtonContent()}
                  </Button>
                {!canViewContent && profile?.is_private ? <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <GlassButton size="sm" disabled>
            <MessageCircle className="w-4 h-4 mr-2" />
            Messaggio
          </GlassButton>
        </span>
      </TooltipTrigger>
      <TooltipContent>Segui per inviare messaggi</TooltipContent>
    </Tooltip>
  </TooltipProvider> : <GlassButton size="sm" onClick={handleSendMessage}>
    <MessageCircle className="w-4 h-4 mr-2" />
    Messaggio
  </GlassButton>}
                <GlassButton size="sm" onClick={handleShareProfile}>
  <Share2 className="w-4 h-4 mr-2" />
  Condividi
                </GlassButton>
                <DropdownMenu>
  <DropdownMenuTrigger asChild>
    <GlassButton size="sm">
      <MoreHorizontal className="w-4 h-4" />
    </GlassButton>
  </DropdownMenuTrigger>
  <DropdownMenuContent align="end">
    <DropdownMenuItem onClick={async () => {
                      const ok = await copyToClipboard(`${window.location.origin}/app/user/${profile?.username}`);
                      if (ok) toast.success('Link copiato negli appunti!');else toast.error('Impossibile copiare il link');
                    }}>
      Copia link profilo
    </DropdownMenuItem>
    <DropdownMenuSeparator />
    <DropdownMenuItem disabled>
      Segnala utente (presto)
    </DropdownMenuItem>
  </DropdownMenuContent>
                </DropdownMenu>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-xl font-bold text-white">{formatCount(profile.posts_count)}</p>
                  <p className="text-sm text-muted-foreground">Post</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-white">{formatCount(profile.pets_count)}</p>
                  <p className="text-sm text-muted-foreground">Pet</p>
                </div>
                <Link to={`/app/user/${profile.username}/followers`} className="text-center hover:text-primary transition-colors">
                  <p className="text-xl font-bold text-white">{formatCount(profile.followers_count)}</p>
                  <p className="text-sm text-muted-foreground">Follower</p>
                </Link>
                <Link to={`/app/user/${profile.username}/following`} className="text-center hover:text-primary transition-colors">
                  <p className="text-xl font-bold text-white">{formatCount(profile.following_count)}</p>
                  <p className="text-sm text-muted-foreground">Seguiti</p>
                </Link>
              </div>

              {/* Bio and Info */}
              <div className="space-y-2">
                {profile.bio && <p className="text-white">{profile.bio}</p>}
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  {profile.location && <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{profile.location}</span>
                    </div>}
                  {profile.website && <div className="flex items-center gap-1">
                      <LinkIcon className="w-4 h-4" />
                      <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {profile.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>}
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Iscritto {formatDistanceToNow(new Date(profile.created_at), {
                      addSuffix: true,
                      locale: it
                    })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs */}
      <Tabs defaultValue="posts" className="space-y-6">
        <TabsList className="w-full glass border-white/20">
          <TabsTrigger value="posts" className="flex-1 data-[state=active]:bg-primary">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Post ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="pets" className="flex-1 data-[state=active]:bg-primary">
            <Heart className="w-4 h-4 mr-2" />
            Pet ({pets.length})
          </TabsTrigger>
        </TabsList>

        {/* Posts Tab */}
        <TabsContent value="posts" className="space-y-6">
          {!canViewContent && profile?.is_private ? <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Profilo Privato</h3>
                <p className="text-muted-foreground mb-4">
                  Solo i follower possono vedere i post di @{profile.username}.
                </p>
                {followSystem.followRequestStatus === 'none' && <Button onClick={handleToggleFollow} disabled={followLoading}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Richiedi di seguire
                  </Button>}
                {followSystem.followRequestStatus === 'pending' && <div className="flex items-center justify-center gap-2 text-yellow-400">
                    <Clock className="w-5 h-5" />
                    <span>Richiesta in attesa</span>
                  </div>}
              </CardContent>
            </Card> : posts.length === 0 ? <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Camera className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nessun post pubblico</h3>
                <p className="text-muted-foreground">
                  @{profile?.username} non ha ancora condiviso post pubblici.
                </p>
              </CardContent>
            </Card> : <ProfileGrid posts={posts} />}
        </TabsContent>

        {/* Pets Tab */}
        <TabsContent value="pets" className="space-y-6">
          {!canViewContent && profile?.is_private ? <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Profilo Privato</h3>
                <p className="text-muted-foreground mb-4">
                  Solo i follower possono vedere i pet di @{profile.username}.
                </p>
                {followSystem.followRequestStatus === 'none' && <Button onClick={handleToggleFollow} disabled={followLoading}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Richiedi di seguire
                  </Button>}
                {followSystem.followRequestStatus === 'pending' && <div className="flex items-center justify-center gap-2 text-yellow-400">
                    <Clock className="w-5 h-5" />
                    <span>Richiesta in attesa</span>
                  </div>}
              </CardContent>
            </Card> : pets.length === 0 ? <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nessun pet pubblico</h3>
                <p className="text-muted-foreground">
                  @{profile?.username} non ha pet pubblici da mostrare.
                </p>
              </CardContent>
            </Card> : <div className="grid gap-4 md:grid-cols-2">
              {pets.map(pet => <PetCard key={pet.id} pet={pet} showActions={false} />)}
            </div>}
        </TabsContent>
      </Tabs>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Condividi profilo</DialogTitle>
            <DialogDescription>
              Copia il link e condividilo dove vuoi.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={`${window.location.origin}/app/user/${profile?.username}`} className="flex-1" />
            <Button onClick={async () => {
            const ok = await copyToClipboard(`${window.location.origin}/app/user/${profile?.username}`);
            if (ok) toast.success('Link copiato negli appunti!');else toast.error('Impossibile copiare il link');
          }}>
              Copia
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  </div>;
};
export default UserProfile;